/**
 * MobileRowActionsMenu — the compact-density overflow menu for a mobile card
 * (TV1-2294 ticket 23).
 *
 * WHY IT EXISTS. A comfortable card gives its actions a dedicated bottom row:
 * a 1px divider, 12px of padding and a 26px icon strip, ~39px in total. On the
 * publications card that strip currently holds EIGHT icons (ticket 28 restored
 * "Sincronizar desde canal"), measured at 236px inside a ~280px row. Compact
 * targets roughly half the card height, so a 39px action row is not affordable
 * — but the ticket's hard rule is that COMPACT MUST NOT DROP CAPABILITY. The
 * ticket sanctions exactly this escape hatch: collapse the strip into an
 * overflow menu, drop nothing.
 *
 * ONE COMPONENT, NOT THREE. Publications and inventory both feed it; orders
 * keeps its own `OrderActionsMenu` (see `OrderMobileRow`) because forking that
 * component would duplicate its capability gates — the defect ticket 19 of this
 * same PR had to repair.
 *
 * THE CALLER BUILDS ONE ARRAY AND RENDERS IT BOTH WAYS. Each card derives a
 * single `actions` array — same conditions, same handlers, same labels — and
 * renders it as the icon strip at comfortable density and through this menu at
 * compact. There is therefore no second list to keep in sync, which is the only
 * way an action can end up reachable in one density and not the other.
 *
 * LABELS ARE A NET GAIN ON TOUCH. The comfortable strip identifies its actions
 * with hover `Tooltip`s, which a touch screen cannot show; the menu prints the
 * same strings as visible text and is keyboard/AT reachable.
 */

import type { ReactNode } from 'react';
import { ActionIcon, Menu } from '@mantine/core';
import { IconDots } from '@tabler/icons-react';
import classes from './MobileRowActionsMenu.module.css';

export type MobileRowAction = {
  /** Stable key — also the React key. */
  key: string;
  /** Already-translated label. Shared with the comfortable strip's tooltip. */
  label: string;
  /** Rendered as the item's left section. Size it 16px, like the strip does. */
  icon: ReactNode;
  onClick: () => void;
  /** Mantine colour for destructive items (e.g. `red` for "Dar de baja"). */
  color?: string;
  disabled?: boolean;
  /**
   * Optional stable selector, applied to the comfortable strip's `ActionIcon`
   * AND to this menu's item — the two densities never render at once for the
   * same row, so one spec locator works in both.
   */
  testId?: string;
};

export type MobileRowActionsMenuProps = {
  'actions': MobileRowAction[];
  /** Accessible name for the kebab target, e.g. "Más acciones". */
  'ariaLabel': string;
  'data-testid'?: string;
};

export const MobileRowActionsMenu = ({
  actions,
  ariaLabel,
  'data-testid': testId
}: MobileRowActionsMenuProps ) => {
  // A card with no actions renders no affordance at all rather than a dead
  // kebab that opens an empty dropdown.
  if ( actions.length === 0 ) return null;

  return (
    <Menu withinPortal position="bottom-end" offset={4} shadow="md" width={240}>
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          radius="xs"
          aria-label={ariaLabel}
          title={ariaLabel}
          data-testid={testId}
          /* The kebab is a SIBLING of the card's tappable body, so this guard is
             belt-and-braces — but the card is a click target and a stray bubble
             would open the detail view behind the menu. */
          onClick={( event ) => { event.stopPropagation(); }}
        >
          <IconDots size={18} stroke={1.8} />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>
        {actions.map(( action ) => (
          <Menu.Item
            key={action.key}
            leftSection={action.icon}
            color={action.color}
            disabled={action.disabled}
            className={classes.item}
            data-testid={action.testId}
            onClick={( event ) => {
              event.stopPropagation();
              if ( !action.disabled ) action.onClick();
            }}
          >
            {action.label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
};

export default MobileRowActionsMenu;
